import React, { useEffect, useState, Fragment } from "react";
import { Dialog, Transition, Menu } from "@headlessui/react";
import MultiSelect from "../../../../components/MultiSelect";
import PaginationView from "../../../../components/molecules/Pagination";
import Location from "../../../../api/Location";
import User from "../../../../api/User";
import RBAC from "../../../../api/RBAC";
import { toast } from "react-hot-toast";
import { TrashIcon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

/** Catalog / API role id for a user-role row. Prefer feature_role_id from API over pivot id in role.id. */
const getUserRoleDefinitionId = (role) => {
  if (role == null) return undefined;
  return (
    role.feature_role_id ??
    role.role_id ??
    role.role?.id ??
    role.id
  );
};

const RBACUserAttachTab = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationUsers, setLocationUsers] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [existingRoles, setExistingRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [attachingUsers, setAttachingUsers] = useState({}); // Map of userId_roleId -> loading state
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [selectedUserForAttach, setSelectedUserForAttach] = useState(null);
  const [selectedRoleToAttach, setSelectedRoleToAttach] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [metaData, setMetaData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [roleToDeleteDisplay, setRoleToDeleteDisplay] = useState(null);
  const [userForDelete, setUserForDelete] = useState(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const [showEditDefaultRoleModal, setShowEditDefaultRoleModal] = useState(false);
  const [defaultRoleToEdit, setDefaultRoleToEdit] = useState(null);
  const [userForEdit, setUserForEdit] = useState(null);
  const [selectedNewDefaultRole, setSelectedNewDefaultRole] = useState(null);
  const [isChangingDefaultRole, setIsChangingDefaultRole] = useState(false);

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await Location.getAll();
      const locationsData = response.data.data || response.data || [];
      const formattedLocations = locationsData.map((location) => ({
        label: location.name || location.location_name || `Location ${location.id}`,
        value: location.id,
      }));
      setLocations(formattedLocations);
    } catch (error) {
      console.error("Error loading locations:", error);
      toast.error("Failed to load locations");
    }
    setLoadingLocations(false);
  };

  const loadRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const response = await RBAC.getRolesList();
      const rolesData = response.data
        .filter((role) => role.id !== null) // Only include roles with id
        .map((role) => ({
          label: role.role_name || role.name || `Role ${role.role_id}`,
          value: role.id,
          role_id: role.role_id,
          category_type: role.category_type || "custom",
        }));
      setExistingRoles(rolesData);
    } catch (error) {
      console.error("Error loading roles:", error);
      toast.error("Failed to load roles");
    }
    setIsLoadingRoles(false);
  };

  const handleLocationChange = async (selected) => {
    setSelectedLocation(selected);
    setLocationUsers([]);
    setCurrentPage(1);
    setMetaData({});
    if (selected && selected.value) {
      await loadUsersByLocation(selected.value, 1);
    }
  };

  const loadUsersByLocation = async (locationId, page = 1) => {
    setLoadingUsers(true);
    try {
      const queryParams = `location_id=${locationId}&page=${page}&limit=${pageSize}`;
      const response = await User.getAll(queryParams);
      const usersData = response.data.data || response.data || [];
      setLocationUsers(usersData);
      setMetaData(response.data.meta || {});
      setCurrentPage(page);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users for this location");
      setLocationUsers([]);
      setMetaData({});
    }
    setLoadingUsers(false);
  };

  const handlePageChange = (page) => {
    if (selectedLocation && selectedLocation.value) {
      loadUsersByLocation(selectedLocation.value, page);
    }
  };

  const handleOpenAttachModal = (user) => {
    setSelectedUserForAttach(user);
    setSelectedRoleToAttach([]);
    setShowAttachModal(true);
  };

  const handleAttachRole = async () => {
    if (!selectedUserForAttach || !selectedRoleToAttach || selectedRoleToAttach.length === 0) {
      toast.error("Please select at least one role to attach");
      return;
    }
    
    const user = selectedUserForAttach;
    // Get role_id from existingRoles for each selected role
    const roleIds = selectedRoleToAttach.map((selectedRole) => {
      const roleDetails = existingRoles.find((r) => r.value === selectedRole.value);
      return roleDetails?.role_id || selectedRole.role_id || selectedRole.value;
    });
    const loadingKey = `${user.id}_multiple`;
    setAttachingUsers((prev) => ({ ...prev, [loadingKey]: true }));
    
    try {
      if (!selectedLocation) {
        toast.error("Please select a location first");
        setAttachingUsers((prev) => ({ ...prev, [loadingKey]: false }));
        return;
      }
      
      await RBAC.addPermissionsRolesUser({
        user_id: user.id,
        role_id: roleIds,
        location_id: selectedLocation.value,
      });
      
      // Find the role details from existingRoles and update user roles
      const rolesToAdd = selectedRoleToAttach.map((selectedRole) => {
        const roleDetails = existingRoles.find((r) => r.value === selectedRole.value);
        return roleDetails
          ? {
              id: selectedRole.value,
              role_id: roleDetails.role_id,
              feature_role_id: roleDetails.role_id ?? selectedRole.value,
              name: roleDetails.label,
              category_type: roleDetails.category_type || "custom",
            }
          : null;
      }).filter(Boolean);
      
      if (rolesToAdd.length > 0) {
        // Update user roles in the users array
        setLocationUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === user.id
              ? {
                  ...u,
                  roles: [...(u.roles || []), ...rolesToAdd],
                }
              : u
          )
        );
      }
      
      toast.success(`${rolesToAdd.length} role(s) attached to ${user.first_name} ${user.last_name}`);
      setShowAttachModal(false);
      setSelectedUserForAttach(null);
      setSelectedRoleToAttach([]);
    } catch (error) {
      console.error("Error attaching roles:", error);
      toast.error("Failed to attach roles");
    }
    
    setAttachingUsers((prev) => ({ ...prev, [loadingKey]: false }));
  };

  const handleDeleteRole = async () => {
    if (!userForDelete || !roleToDelete) return;
    
    setIsDeletingRole(true);
    try {
      const roleToDetach = existingRoles.find((r) => r.value === roleToDelete);
      const roleIdToUse = roleToDetach?.role_id || roleToDelete;
      
      await RBAC.deleteRoleForUser(userForDelete.id, roleIdToUse);
      toast.success(`Role deleted from ${userForDelete.first_name} ${userForDelete.last_name}`);
      
      // Update user roles in the users array
      setLocationUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userForDelete.id
            ? {
                ...u,
                roles: (u.roles || []).filter((role) => {
                  const defId = getUserRoleDefinitionId(role);
                  const listRole = existingRoles.find((r) => r.role_id === defId || r.value === defId);
                  const rowApiId = listRole?.role_id ?? defId;
                  return rowApiId !== roleIdToUse;
                }),
              }
            : u
        )
      );
      
      // Refresh users list to get updated data
      if (selectedLocation && selectedLocation.value) {
        await loadUsersByLocation(selectedLocation.value, currentPage);
      }
      
      setShowDeleteModal(false);
      setRoleToDelete(null);
      setRoleToDeleteDisplay(null);
      setUserForDelete(null);
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role");
    } finally {
      setIsDeletingRole(false);
    }
  };

  const handleOpenDeleteModal = (user, roleAttachment) => {
    const defId = getUserRoleDefinitionId(roleAttachment);
    const matchingRole = existingRoles.find((r) => r.role_id === defId || r.value === defId);
    const roleValue = matchingRole ? matchingRole.value : defId;
    const displayName =
      roleAttachment.name ||
      roleAttachment.role_name ||
      roleAttachment.role?.name ||
      matchingRole?.label;

    setUserForDelete(user);
    setRoleToDelete(roleValue);
    setRoleToDeleteDisplay(displayName || null);
    setShowDeleteModal(true);
  };

  const handleOpenEditDefaultRoleModal = (user, role) => {
    setUserForEdit(user);
    setDefaultRoleToEdit(role);
    setSelectedNewDefaultRole(null);
    setShowEditDefaultRoleModal(true);
  };

  const handleChangeDefaultRole = async () => {
    if (!userForEdit || !defaultRoleToEdit || !selectedNewDefaultRole) {
      toast.error("Please select a new default role");
      return;
    }

    setIsChangingDefaultRole(true);
    try {
      if (!selectedLocation) {
        toast.error("Please select a location first");
        setIsChangingDefaultRole(false);
        return;
      }

      // Get role IDs
      const previousRoleId = getUserRoleDefinitionId(defaultRoleToEdit);
      const previousRoleFromList = existingRoles.find((r) => r.role_id === previousRoleId || r.value === previousRoleId);
      const previousRoleIdToUse = previousRoleFromList?.role_id || previousRoleId;

      const newRoleFromList = existingRoles.find((r) => r.value === selectedNewDefaultRole.value);
      const newRoleIdToUse = newRoleFromList?.role_id || selectedNewDefaultRole.value;

      // Call the update API
      await RBAC.updateAttachUserRole({
        new_role_id: newRoleIdToUse,
        previous_role_id: previousRoleIdToUse,
        user_id: userForEdit.id,
      });

      toast.success(`Default role changed successfully`);
      
      // Refresh users list to get updated data
      if (selectedLocation && selectedLocation.value) {
        await loadUsersByLocation(selectedLocation.value, currentPage);
      }

      setShowEditDefaultRoleModal(false);
      setDefaultRoleToEdit(null);
      setUserForEdit(null);
      setSelectedNewDefaultRole(null);
    } catch (error) {
      console.error("Error changing default role:", error);
      toast.error("Failed to change default role");
    } finally {
      setIsChangingDefaultRole(false);
    }
  };

  useEffect(() => {
    loadLocations();
    loadRoles();
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Attach Users to Location</h2>
          <p className="text-sm text-gray-600 mt-1">Select a location to view and manage user role attachments</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Select Location
          </label>
          <div className="border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
            <MultiSelect
              key="locations"
              options={locations}
              onChange={handleLocationChange}
              value={selectedLocation}
              menuPlacement={"bottom"}
              isLoading={loadingLocations}
              placeholder="Select a location..."
            />
          </div>
        </div>
      </div>

      {/* Users List */}
      {selectedLocation && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Users for: <span className="text-primary-600">{selectedLocation?.label}</span>
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {loadingUsers ? "Loading users..." : metaData?.total ? `${metaData.total} user(s) found` : `${locationUsers.length} user(s) found`}
            </p>
          </div>

          {loadingUsers ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-sm text-gray-600">Loading users...</p>
            </div>
          ) : locationUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">No users found for this location.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Attached Roles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Available Roles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {locationUsers.map((user) => {
                    const userRoles = (user.roles || []).filter((role) => {
                      const roleName = role.name || role.role_name || role.role?.name;
                      return roleName !== null && roleName !== undefined;
                    });
                    
                    // Get attached role IDs - check both id and role_id fields
                    const attachedRoleIds = userRoles.map((role) => {
                      const defId = getUserRoleDefinitionId(role);
                      const matchingRole = existingRoles.find((r) => r.role_id === defId || r.value === defId);
                      return matchingRole ? matchingRole.value : defId;
                    }).filter(Boolean);
                    
                    // Get available roles (not attached)
                    const availableRoles = existingRoles.filter((role) => {
                      if (!role.label || role.label === null) return false;
                      // Check if this role is already attached
                      return !attachedRoleIds.includes(role.value);
                    });
                    
                    return (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{user.email || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{user.phone || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {userRoles.length > 0 ? (
                              userRoles.map((role) => {
                                const roleName = role.name || role.role_name || role.role?.name;
                                const defId = getUserRoleDefinitionId(role);
                                const categoryType = role.category_type || "default";
                                // Find the matching role in existingRoles to get the correct value
                                const matchingRole = existingRoles.find((r) => r.role_id === defId || r.value === defId);
                                const roleValue = matchingRole ? matchingRole.value : defId;
                                const loadingKey = `${user.id}_${roleValue}`;
                                const isLoading = attachingUsers[loadingKey] || false;
                                const isDefaultRole = categoryType === "default";
                                
                                // Check if this is the last user in the list
                                const isLastUser = locationUsers.indexOf(user) === locationUsers.length - 1;
                                const isLastRole = userRoles.indexOf(role) === userRoles.length - 1;
                                
                                return (
                                  <Menu as="div" key={`${user.id}-${defId}-${role.id ?? roleName}`} className="relative inline-block text-left">
                                    {({ open }) => (
                                      <>
                                        <Menu.Button
                                          className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                          disabled={isLoading}
                                        >
                                          {isDefaultRole && (
                                            <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                              Default
                                            </span>
                                          )}
                                          <span>{roleName}</span>
                                          <ChevronDownIcon className="h-3 w-3 text-gray-500" aria-hidden="true" />
                                        </Menu.Button>
                                        <Transition
                                          as={Fragment}
                                          enter="transition ease-out duration-100"
                                          enterFrom="transform opacity-0 scale-95"
                                          enterTo="transform opacity-100 scale-100"
                                          leave="transition ease-in duration-75"
                                          leaveFrom="transform opacity-100 scale-100"
                                          leaveTo="transform opacity-0 scale-95"
                                        >
                                          <Menu.Items className={`absolute right-0 z-50 w-36 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
                                            isLastUser && isLastRole 
                                              ? 'bottom-full mb-1 origin-bottom-right' 
                                              : 'top-full mt-1 origin-top-right'
                                          }`}>
                                            <div className="py-1">
                                              {isDefaultRole ? (
                                                <Menu.Item>
                                                  {({ active }) => (
                                                    <button
                                                      type="button"
                                                      onClick={() => handleOpenEditDefaultRoleModal(user, role)}
                                                      className={`${
                                                        active ? "bg-gray-100" : ""
                                                      } flex items-center w-full px-3 py-2 text-xs text-gray-700`}
                                                    >
                                                      <i className="fas fa-edit mr-2 text-xs"></i>
                                                      Edit Default role
                                                    </button>
                                                  )}
                                                </Menu.Item>
                                              ) : (
                                                <Menu.Item>
                                                  {({ active }) => (
                                                    <button
                                                      type="button"
                                                      onClick={() => handleOpenDeleteModal(user, role)}
                                                      disabled={isLoading}
                                                      className={`${
                                                        active ? "bg-gray-100" : ""
                                                      } flex items-center w-full px-3 py-2 text-xs text-red-600 ${
                                                        isLoading ? "opacity-50 cursor-not-allowed" : ""
                                                      }`}
                                                    >
                                                      {isLoading ? (
                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-2"></div>
                                                      ) : (
                                                        <TrashIcon className="h-3 w-3 mr-2" />
                                                      )}
                                                      Delete
                                                    </button>
                                                  )}
                                                </Menu.Item>
                                              )}
                                            </div>
                                          </Menu.Items>
                                        </Transition>
                                      </>
                                    )}
                                  </Menu>
                                );
                              })
                            ) : (
                              <span className="text-sm text-gray-400">No roles attached</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {availableRoles.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => handleOpenAttachModal(user)}
                              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-lg shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                            >
                              <i className="fas fa-link mr-2"></i>
                              Attach
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">All roles attached</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {!loadingUsers && metaData?.total > 0 && (
            <div className="mt-6 flex justify-center">
              <PaginationView
                current={metaData?.current_page || currentPage}
                next={metaData?.to}
                pageSize={metaData?.per_page || pageSize}
                total={metaData?.total}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Attach Role Modal */}
      <Transition.Root show={showAttachModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => { setShowAttachModal(false); setSelectedUserForAttach(null); setSelectedRoleToAttach([]); }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                        Attach Role(s) to {selectedUserForAttach ? `${selectedUserForAttach.first_name} ${selectedUserForAttach.last_name}` : "User"}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 mb-4">
                          Select one or more roles to attach to this user
                        </p>
                        {selectedUserForAttach && (
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Select Role
                              </label>
                              <div className="border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                                <MultiSelect
                                  key="attach-role-select"
                                  options={existingRoles.filter((role) => {
                                    // Filter out roles with null names
                                    if (!role.label || role.label === null) return false;
                                    
                                    // Filter out default roles
                                    if (role.category_type === "default") return false;
                                    
                                    // Filter out already attached roles
                                    const userRoles = selectedUserForAttach.roles || [];
                                    const attachedRoleIds = userRoles.map((r) => {
                                      const defId = getUserRoleDefinitionId(r);
                                      const matchingRole = existingRoles.find((er) => er.role_id === defId || er.value === defId);
                                      return matchingRole ? matchingRole.value : defId;
                                    }).filter(Boolean);
                                    
                                    return !attachedRoleIds.includes(role.value) && !attachedRoleIds.includes(role.role_id);
                                  })}
                                  onChange={setSelectedRoleToAttach}
                                  value={selectedRoleToAttach}
                                  menuPlacement={"bottom"}
                                  placeholder="Select role(s) to attach..."
                                  isMulti={true}
                                />
                              </div>
                            </div>
                            {existingRoles.filter((role) => {
                              // Filter out roles with null names
                              if (!role.label || role.label === null) return false;
                              
                              // Filter out default roles
                              if (role.category_type === "default") return false;
                              
                              // Filter out already attached roles
                              const userRoles = selectedUserForAttach.roles || [];
                              const attachedRoleIds = userRoles.map((r) => {
                                const defId = getUserRoleDefinitionId(r);
                                const matchingRole = existingRoles.find((er) => er.role_id === defId || er.value === defId);
                                return matchingRole ? matchingRole.value : defId;
                              }).filter(Boolean);
                              
                              return !attachedRoleIds.includes(role.value) && !attachedRoleIds.includes(role.role_id);
                            }).length === 0 && (
                              <p className="text-sm text-gray-400 text-center py-4">All available roles are already attached</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1"
                      onClick={() => { setShowAttachModal(false); setSelectedUserForAttach(null); setSelectedRoleToAttach([]); }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center items-center rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAttachRole}
                      disabled={!selectedRoleToAttach || selectedRoleToAttach.length === 0 || (selectedUserForAttach && attachingUsers[`${selectedUserForAttach.id}_multiple`])}
                    >
                      {selectedUserForAttach && attachingUsers[`${selectedUserForAttach.id}_multiple`] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Attaching...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-link mr-2"></i>
                          Attach {selectedRoleToAttach.length > 0 ? `${selectedRoleToAttach.length} ` : ''}Role{selectedRoleToAttach.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userForDelete && roleToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setShowDeleteModal(false);
                setRoleToDelete(null);
                setRoleToDeleteDisplay(null);
                setUserForDelete(null);
              }}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Role</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setRoleToDelete(null);
                    setRoleToDeleteDisplay(null);
                    setUserForDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isDeletingRole}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-2">
                  Are you sure you want to delete this role from {userForDelete.first_name} {userForDelete.last_name}?
                </p>
                {roleToDelete && (
                    <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {roleToDeleteDisplay ||
                          existingRoles.find((r) => r.value === roleToDelete)?.label ||
                          "this role"}
                      </p>
                    </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setRoleToDelete(null);
                    setRoleToDeleteDisplay(null);
                    setUserForDelete(null);
                  }}
                  disabled={isDeletingRole}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteRole}
                  disabled={isDeletingRole}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeletingRole ? (
                    <>
                      <i className="fad fa-circle-notch fa-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Default Role Modal */}
      {showEditDefaultRoleModal && userForEdit && defaultRoleToEdit && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setShowEditDefaultRoleModal(false);
                setDefaultRoleToEdit(null);
                setUserForEdit(null);
              }}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Change Default Role</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Select a new default role for this user
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEditDefaultRoleModal(false);
                    setDefaultRoleToEdit(null);
                    setUserForEdit(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-4">
                  Current default role: <span className="font-medium">{defaultRoleToEdit.name || defaultRoleToEdit.role_name || defaultRoleToEdit.role?.name}</span>
                </p>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select New Default Role
                  </label>
                  <div className="border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                    <MultiSelect
                      key="edit-default-role"
                      options={existingRoles.filter((role) => {
                        // Filter to show only default roles, excluding the current one
                        if (role.category_type !== "default") return false;
                        const currentRoleId = getUserRoleDefinitionId(defaultRoleToEdit);
                        return role.role_id !== currentRoleId && role.value !== currentRoleId;
                      })}
                      onChange={(selected) => {
                        setSelectedNewDefaultRole(selected);
                      }}
                      value={selectedNewDefaultRole}
                      menuPlacement={"bottom"}
                      placeholder="Select a new default role..."
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditDefaultRoleModal(false);
                    setDefaultRoleToEdit(null);
                    setUserForEdit(null);
                    setSelectedNewDefaultRole(null);
                  }}
                  disabled={isChangingDefaultRole}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleChangeDefaultRole}
                  disabled={isChangingDefaultRole || !selectedNewDefaultRole}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isChangingDefaultRole ? (
                    <>
                      <i className="fad fa-circle-notch fa-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-1"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RBACUserAttachTab;
